// Central registry for /blog posts (git-based). Add new entries here.

import {
  CAPTURE_PHOTO_TABLE,
  CAPTURE_TIPS,
  OFFICIAL_ARTICLE_SLUGS,
} from '../Documentation/learnContent';
import { applyGuideContentDates } from './blogGuideDates';

export const BLOG_BASE = '/blog';

/** Posts registered for SEO/routing but not yet published (hidden from blog index and related links). */
export const BLOG_STUB_SLUGS = new Set([
  'drone-to-3d-model-without-photogrammetry',
  'gaussian-splatting-vs-matterport',
  'gaussian-splatting-vs-photogrammetry',
  '3d-products-for-shopify',
  '360-video-to-3d-scene',
]);

/** @param {string} slug */
export function blogPath(slug) {
  return `${BLOG_BASE}/${slug}`;
}

/**
 * @param {string} text
 * @returns {number}
 */
export function estimateReadMinutesFromPlainText(text) {
  if (!text || typeof text !== 'string') return 1;
  const words = text
    .trim()
    .split(/\s+/)
    .filter(Boolean).length;
  return Math.max(1, Math.round(words / 200));
}

const CAPTURE_GUIDE_EXTRA =
  'Camera settings and flight path Follow the flight path while you move around the subject then use the table as a planning guide. ' +
  'How many photos Use this as a guide for the number of photos by scene type. Scene type Recommended photos. ' +
  'Flight path paint the subject with your camera. Capture checklist Follow these eight points every time you capture. ' +
  'They address lighting focus overlap and scene cleanliness. Info This guide applies to hand-held and gimbal stills. ' +
  'For drone footage see the aerial capture guide.';

function captureGuideWordSource() {
  const table = CAPTURE_PHOTO_TABLE.map((r) => `${r.scene} ${r.videoDuration} ${r.photos}`).join(' ');
  const tips = CAPTURE_TIPS.map((t) => `${t.title} ${t.body}`).join(' ');
  return `${CAPTURE_GUIDE_EXTRA} ${table} ${tips}`;
}

const AERIAL_FOOTAGE_TO_3DGS_PLAIN =
  'Watch the walkthrough above then follow the steps below to turn aerial or drone video into a 3D Gaussian splat on Volugraph. ' +
  'Why aerial video works for 3DGS Aerial passes give wide context around buildings sites and landscapes. ' +
  'When overlap and lighting are good video from a drone or elevated gimbal can reconstruct large outdoor scenes that would take far longer on foot. ' +
  'What you need Smooth stable footage with strong overlap between frames. Consistent altitude and gimbal angle within each pass. ' +
  'Even lighting when possible avoid harsh single-direction shadows across the whole site. A Volugraph account and a project to upload into. ' +
  'Workflow overview Capture Fly your pattern orbit or parallel sweeps with enough overlap. See the aerial capture guide for flight planning detail. ' +
  'Upload Export your clip at full resolution trim takeoff and landing wobble if needed then upload the video to your Volugraph project. ' +
  'Process Volugraph runs structure-from-motion and Gaussian splat training in the cloud. Most aerial captures finish within about an hour depending on length and resolution. ' +
  'Review and share Open the splat in the browser navigate freely and share a link with your team or embed it on a project page. ' +
  'Tips for cleaner splats Prefer one continuous session per lighting condition. Add a second pass at the same height instead of mixing very different altitudes. ' +
  'If the result looks soft increase overlap reduce vibration or wait for softer light before re-flying.';

/**
 * @typedef {Object} BlogPostMeta
 * @property {string} slug
 * @property {string} categoryLabel
 * @property {string} title
 * @property {string} [titleLine1] First hero title line (plain)
 * @property {string} [titleLine2] Second hero title line (gradient highlight)
 * @property {string} description
 * @property {string} [publishedAt] ISO date (from guide content git history)
 * @property {string} [updatedAt] ISO date (from guide content git history)
 * @property {string} [publishedLabel]
 * @property {string} [updatedLabel]
 * @property {string} seoTitle
 * @property {string} seoDescription
 * @property {string} [heroImage]
 * @property {() => number} readMinutes
 */

/** @type {BlogPostMeta[]} */
export const BLOG_POSTS = [
  {
    slug: OFFICIAL_ARTICLE_SLUGS.capture,
    categoryLabel: 'GUIDE',
    title: 'How To Capture Guide',
    titleLine1: 'How to',
    titleLine2: 'Capture Guide',
    description:
      'Step-by-step reference for how many stills to shoot, the one “spiral” rule that works for most users, and a full pre-flight checklist.',
    seoTitle: 'How To Capture Guide – Blog',
    seoDescription:
      'Camera settings, flight path, photo counts by scene, and an eight-point capture checklist for 3D Gaussian Splatting.',
    heroImage: 'capture',
    readMinutes: () => estimateReadMinutesFromPlainText(captureGuideWordSource()),
  },
  {
    slug: OFFICIAL_ARTICLE_SLUGS.aerialFootage,
    categoryLabel: 'GUIDE',
    title: 'Aerial Footage to 3D Gaussian Splat',
    description:
      'Watch the full workflow, then upload drone or aerial video to Volugraph and share an interactive 3D Gaussian splat in the browser.',
    seoTitle: 'Aerial Footage to 3D Gaussian Splat – Volugraph Learn',
    seoDescription:
      'Turn aerial and drone video into a 3D Gaussian splat on Volugraph: capture tips, upload workflow, processing, and sharing.',
    heroImage: 'aerial',
    readMinutes: () => estimateReadMinutesFromPlainText(AERIAL_FOOTAGE_TO_3DGS_PLAIN),
  },
  {
    slug: OFFICIAL_ARTICLE_SLUGS.ecommerce,
    categoryLabel: 'GUIDE',
    title: 'E-Commerce Products',
    description:
      'Capture products from video, process them in Volugraph, and share or embed interactive 3D for online retail.',
    seoTitle: 'E-Commerce Products – Volugraph Learn',
    seoDescription:
      'Turn product video into interactive 3D Gaussian splats for e-commerce listings, pitches, and digital showrooms.',
    heroImage: 'comparison',
    readMinutes: () => 4,
  },
  {
    slug: 'drone-to-3d-model-without-photogrammetry',
    categoryLabel: 'GUIDE',
    title: 'Drone to 3D Model Without Photogrammetry',
    description: 'How aerial video becomes a Gaussian splat in the browser. Workflow overview. Full guide coming soon.',
    publishedLabel: 'May 1, 2026',
    updatedLabel: 'May 1, 2026',
    seoTitle: 'Drone to 3D Model Without Photogrammetry – Volugraph',
    seoDescription:
      'Turn drone footage into an interactive 3D Gaussian splat without a desktop photogrammetry pipeline. Coming soon.',
    heroImage: 'aerial',
    readMinutes: () => 1,
  },
  {
    slug: 'gaussian-splatting-vs-matterport',
    categoryLabel: 'COMPARISON',
    title: 'Gaussian Splatting vs Matterport',
    description: 'When browser-native Gaussian splats fit better than hardware tour capture. Full comparison coming soon.',
    publishedLabel: 'May 1, 2026',
    updatedLabel: 'May 1, 2026',
    seoTitle: 'Gaussian Splatting vs Matterport – Volugraph',
    seoDescription:
      'Compare 3D Gaussian splatting from video with Matterport-style capture for outdoor, product, and location use cases.',
    heroImage: 'comparison',
    readMinutes: () => 1,
  },
  {
    slug: 'gaussian-splatting-vs-photogrammetry',
    categoryLabel: 'COMPARISON',
    title: 'Gaussian Splatting vs Photogrammetry',
    description: 'Splats vs mesh photogrammetry for review, sharing, and speed. Full comparison coming soon.',
    publishedLabel: 'May 1, 2026',
    updatedLabel: 'May 1, 2026',
    seoTitle: 'Gaussian Splatting vs Photogrammetry – Volugraph',
    seoDescription:
      'Understand trade-offs between Gaussian splatting and traditional photogrammetry meshes for reality capture.',
    heroImage: 'comparison',
    readMinutes: () => 1,
  },
  {
    slug: '3d-products-for-shopify',
    categoryLabel: 'GUIDE',
    title: '3D Products for Shopify',
    description: 'Embed interactive product splats on Shopify listings. Full guide coming soon.',
    publishedLabel: 'May 1, 2026',
    updatedLabel: 'May 1, 2026',
    seoTitle: '3D Products for Shopify – Volugraph',
    seoDescription:
      'Capture products as 3D Gaussian splats and share or embed them on Shopify and e-commerce pages.',
    heroImage: 'comparison',
    readMinutes: () => 1,
  },
  {
    slug: '360-video-to-3d-scene',
    categoryLabel: 'GUIDE',
    title: '360 Video to 3D Scene',
    description: 'From immersive video passes to navigable 3D captures. Full guide coming soon.',
    publishedLabel: 'May 1, 2026',
    updatedLabel: 'May 1, 2026',
    seoTitle: '360 Video to 3D Scene – Volugraph',
    seoDescription:
      'Turn 360 and walkthrough video into explorable 3D Gaussian splat scenes in the browser.',
    heroImage: 'capture',
    readMinutes: () => 1,
  },
];

function enrichPost(post) {
  return applyGuideContentDates(post);
}

const postsBySlug = new Map(BLOG_POSTS.map((p) => [p.slug, enrichPost(p)]));

/** @param {string} slug */
export function getBlogPostBySlug(slug) {
  return postsBySlug.get(slug) || null;
}

export function listBlogPosts() {
  return BLOG_POSTS.filter((p) => !BLOG_STUB_SLUGS.has(p.slug)).map(enrichPost);
}

/** @param {BlogPostMeta} post */
export function getReadMinutesForPost(post) {
  return typeof post.readMinutes === 'function' ? post.readMinutes() : 1;
}
